/**
 * The arts' voices (docs/research/C0_SOUND.md §3): each art (and each kind of monster or boss) is a family — a weapon
 * that cuts the air its own way, a motif that says which art it is (wind and leaves, cloud puffs, blood, ink and
 * paper, ice, temple bells, chains, petals, moonlight…), and a home pitch. A move's sound (sfx/moves/compose.ts) is
 * built from its family's layers in time: wind-up (`gather`), the cut (`swing`), the blow (`accent`), the dying away
 * (`tail`); heals and buffs bloom (`bloom`).
 */
import { BELL, type SfxKit } from '../kit';
import { bowl, chime, drum, gong, note, paper, ring, strum, whoosh, woodblock } from '../voices';

export type WeaponVoice =
  | 'jian'
  | 'dao'
  | 'palm'
  | 'fist'
  | 'claw'
  | 'finger'
  | 'sleeve'
  | 'thrown'
  | 'beast'
  | 'heavy';

export type Motif =
  | 'wind'
  | 'cloud'
  | 'taiji'
  | 'snow'
  | 'bell'
  | 'steel'
  | 'shadow'
  | 'ink'
  | 'qin'
  | 'blood'
  | 'tiger'
  | 'sea'
  | 'chain'
  | 'petal'
  | 'moon'
  | 'dance'
  | 'dragon'
  | 'needle'
  | 'thunder'
  | 'stone'
  | 'fire'
  | 'water'
  | 'hiss'
  | 'dark';

export interface Family {
  readonly label: string;
  readonly weapon: WeaponVoice;
  readonly motif: Motif;
  /** Home pitch (Hz) of the pitched layers: each art in its own register. */
  readonly root: number;
}

export const FAMILIES: Readonly<Record<string, Family>> = {
  'nine-swords': { label: 'ร่องศิลา (อัสนี)', weapon: 'jian', motif: 'thunder', root: 440 },
  'cloud-palm': { label: 'เมฆาคล้อย', weapon: 'palm', motif: 'cloud', root: 261.6 },
  'breeze-sword': { label: 'ไผ่ห่มหมอก', weapon: 'jian', motif: 'wind', root: 587.3 },
  'drift-fist': { label: 'หมอกวน', weapon: 'palm', motif: 'taiji', root: 220 },
  'drift-sword': { label: 'วงธารา', weapon: 'jian', motif: 'taiji', root: 330 },
  'snow-sword': { label: 'ธารเยือกแข็ง', weapon: 'jian', motif: 'snow', root: 1174.7 },
  'bell-fist': { label: 'วัชระอรหันต์', weapon: 'fist', motif: 'bell', root: 196 },
  'lamp-hook-sabre': { label: 'ผีเสื้อเหล็ก', weapon: 'dao', motif: 'steel', root: 370 },
  'quiet-palm': { label: 'จันทร์มัว', weapon: 'claw', motif: 'shadow', root: 207.7 },
  'ink-slate': { label: 'พู่กันหมึกสน', weapon: 'jian', motif: 'ink', root: 392 },
  'heat-sabre': { label: 'ดาบชาด', weapon: 'dao', motif: 'blood', root: 146.8 },
  'hum-palm': { label: 'ศิลาจารึก', weapon: 'palm', motif: 'bell', root: 174.6 },
  'founder-sabre': { label: 'พิชิตมาร', weapon: 'jian', motif: 'stone', root: 233.1 },
  'chain-hold': { label: 'หัตถ์จับกุม', weapon: 'fist', motif: 'chain', root: 277.2 },
  'quick-draw-sabre': { label: 'ดาบโคมเหล็ก', weapon: 'dao', motif: 'petal', root: 493.9 },
  'listening-blade': { label: 'จันทร์เสน่หา', weapon: 'jian', motif: 'moon', root: 659.3 },
  'chorus': { label: 'เพลงหมู่เงาจันทร์', weapon: 'sleeve', motif: 'dance', root: 523.3 },
  'pulse-seal': { label: 'นิ้วกระบี่', weapon: 'finger', motif: 'qin', root: 349.2 },
  'one-cut': { label: 'ปราชญ์ไร้ถ้อย', weapon: 'jian', motif: 'ink', root: 293.7 },
  'tongs-claw': { label: 'พยัคฆ์โลหิต', weapon: 'claw', motif: 'tiger', root: 110 },
  'cliff-push-sabre': { label: 'ผาแดง', weapon: 'dao', motif: 'sea', root: 123.5 },
  'dragon-palm': { label: 'มังกรพายุ', weapon: 'palm', motif: 'dragon', root: 155.6 },
  // prototype staff art (data/staffArt.ts, hidden): the voice is a placeholder until the staff phase
  'tide-staff': { label: 'ไม้พลองคลื่นไผ่', weapon: 'heavy', motif: 'sea', root: 392 },
  'hidden-arts': { label: 'อาวุธลับ', weapon: 'thrown', motif: 'needle', root: 830.6 },
  beast: { label: 'สัตว์ป่า', weapon: 'beast', motif: 'hiss', root: 98 },
  vermin: { label: 'สัตว์พิษ', weapon: 'claw', motif: 'hiss', root: 466.2 },
  outlaw: { label: 'โจรป่า', weapon: 'dao', motif: 'stone', root: 185 },
  thrower: { label: 'นักซัดอาวุธ', weapon: 'thrown', motif: 'needle', root: 740 },
  pirate: { label: 'โจรสลัด', weapon: 'dao', motif: 'water', root: 164.8 },
  ape: { label: 'วานรยักษ์', weapon: 'heavy', motif: 'stone', root: 82.4 },
  firebird: { label: 'วิหคเพลิง', weapon: 'beast', motif: 'fire', root: 622.3 },
  turtle: { label: 'เต่าอัสนี', weapon: 'heavy', motif: 'water', root: 73.4 },
  demon: { label: 'มารทมิฬ', weapon: 'heavy', motif: 'dark', root: 69.3 },
};

/** A small bit of metal (a link, an anklet bell, a needle): two partials, cheap. */
export const TINK: readonly (readonly [number, number, number])[] = [
  [1, 1, 1],
  [2.76, 0.45, 0.6],
];

/** Per-move knobs (sfx/moves/plan.ts). */
export interface Vary {
  /** Pentatonic step off the family's root. */
  deg: number;
  /** Multiplies the airy layers' filter frequencies. */
  bright: number;
  /** Multiplies the tails' length. */
  tail: number;
  /** 0–3: which variant of the motif. */
  variant: number;
  /** Which way the cut goes (+1 up, −1 down): alternates per blow. */
  dir?: number;
}

const pitch = (f: Family, v: Vary, step = 0) => note(f.root, v.deg + step);

// ───────── the cut (one per blow) ─────────

/** The weapon tearing the air, ending on the blow at `at + len`. */
export function swing(k: SfxKit, f: Family, at: number, w: number, v: Vary): void {
  const b = v.bright;
  const vol = 0.18 + w * 0.06;
  const up = (v.dir ?? 1) > 0;
  const t = Math.max(0, at);
  switch (f.weapon) {
    case 'jian':
      whoosh(k, { at: t, dur: 0.2, f0: (up ? 900 : 1300) * b, f1: (up ? 4800 : 3600) * b, f2: 2200 * b, vol, q: 3 });
      ring(k, { f: 2400 * b + v.variant * 160, at: t + 0.12, dur: 0.25, vol: 0.022 });
      break;
    case 'dao':
      whoosh(k, { at: t, dur: 0.26, f0: 350 * b, f1: (up ? 2600 : 2100) * b, f2: 800 * b, vol: vol * 1.15, q: 1.6 });
      k.thump({ f0: 150, f1: 55, at: t + 0.15, dur: 0.18, vol: 0.18 + w * 0.06 });
      break;
    case 'palm':
      whoosh(k, { at: t, dur: 0.24, f0: 300 * b, f1: 1300 * b, f2: 500, vol: vol * 0.8, q: 1.5, color: 'pink' });
      k.thump({ f0: 130, f1: 45, at: t + 0.14, dur: 0.28, vol: 0.32 + w * 0.08 });
      break;
    case 'fist':
      whoosh(k, { at: t + 0.04, dur: 0.14, f0: 500 * b, f1: 1900 * b, f2: 700, vol, q: 2 });
      k.thump({ f0: 180, f1: 60, at: t + 0.14, dur: 0.14, vol: 0.34 + w * 0.06 });
      k.click({ f: 1500 * b, at: t + 0.14, vol: 0.12 });
      break;
    case 'claw':
      for (let i = 0; i < 3; i++)
        k.noise({ at: t + 0.08 + i * 0.025, dur: 0.07, vol: 0.16, attack: 0.004, f: (2200 - i * 300) * b, f1: 900, q: 3 });
      k.noise({ color: 'crackle', at: t + 0.1, dur: 0.12, vol: 0.4, type: 'bandpass', f: 1600 * b, q: 0.8 });
      break;
    case 'finger':
      k.tone({ f: 2400 * b, f1: 3800 * b, at: t + 0.06, dur: 0.1, vol: 0.04 });
      k.click({ f: 3000 * b, at: t + 0.15, vol: 0.16 });
      whoosh(k, { at: t + 0.04, dur: 0.12, f0: 1500 * b, f1: 5200 * b, f2: 3000, vol: 0.1, q: 4 });
      break;
    case 'sleeve':
      k.noise({
        color: 'pink',
        at: t,
        dur: 0.3,
        vol: vol * 1.1,
        attack: 0.1,
        f: 600 * b,
        f1: 1700 * b,
        f2: 700,
        sweep: 0.16,
        q: 1.2,
        trem: [14 + v.variant * 3, 0.6],
      });
      break;
    case 'thrown':
      k.tone({ f: 4200 * b, f1: 2300 * b, at: t, dur: 0.14, vol: 0.035 });
      k.noise({ at: t, dur: 0.1, vol: 0.06, type: 'highpass', f: 5500 });
      break;
    case 'beast':
      k.tone({ type: 'sawtooth', f: 95 * b, f1: 70, at: t, dur: 0.3, vol: 0.07, attack: 0.04, out: k.filter('lowpass', 650) });
      whoosh(k, { at: t + 0.04, dur: 0.18, f0: 400, f1: 1700 * b, f2: 600, vol: 0.18, q: 1.6 });
      break;
    case 'heavy':
      whoosh(k, { at: t, dur: 0.36, f0: 120, f1: 700 * b, f2: 160, vol: 0.26, q: 1, color: 'pink' });
      k.thump({ f0: 95, f1: 30, at: t + 0.22, dur: 0.4, vol: 0.45 });
      break;
  }
}

// ───────── the blow's mark (which art it is) ─────────

/** The art's signature on a blow, at `at`, by weight 0–2. */
export function accent(k: SfxKit, f: Family, at: number, w: number, v: Vary): void {
  const p = pitch(f, v);
  const s = 0.8 + w * 0.2;
  const b = v.bright;
  switch (f.motif) {
    case 'wind':
      k.noise({ color: 'crackle', at, dur: 0.12 + v.variant * 0.03, vol: 0.4 * s, type: 'highpass', f: 2800 * b });
      whoosh(k, { at, dur: 0.3, f0: 700 * b, f1: 2400 * b, f2: 900, vol: 0.12 * s, q: 1.4, color: 'pink' });
      if (v.variant % 2) woodblock(k, { f: 880 + v.deg * 40, at: at + 0.02, vol: 0.06 });
      break;
    case 'cloud':
      k.noise({ color: 'pink', at, dur: 0.3, vol: 0.32 * s, attack: 0.03, type: 'lowpass', f: 800 * b, f1: 300 });
      k.thump({ f0: 100, f1: 45, at, dur: 0.25, vol: 0.18 * s });
      k.tone({ f: p, f1: p * 0.75, at: at + 0.02, dur: 0.3, vol: 0.03 });
      break;
    case 'taiji': {
      const l = k.pan(-0.6);
      const r = k.pan(0.6);
      k.tone({ f: p, at, dur: 0.4, vol: 0.05 * s, attack: 0.02, out: v.variant % 2 ? l : r });
      k.tone({ f: p * 1.5, at: at + 0.06, dur: 0.38, vol: 0.04 * s, attack: 0.02, out: v.variant % 2 ? r : l });
      k.thump({ f0: 120, f1: 50, at, dur: 0.2, vol: 0.2 * s });
      break;
    }
    case 'snow':
      k.noise({ dur: 0.07, at, vol: 0.25 * s, attack: 0.001, type: 'highpass', f: 4000 });
      crystals(k, 4 + w * 3 + v.variant, at, 0.2, 0.035);
      break;
    case 'bell':
      k.thump({ f0: 120, f1: 40, at, dur: 0.3, vol: 0.35 * s });
      k.metal({ f: p * 0.5, at, dur: 0.7 + w * 0.4, vol: 0.07 * s, partials: BELL, drift: 0.004 });
      break;
    case 'stone':
      k.thump({ f0: 105, f1: 34, at, dur: 0.35, vol: 0.4 * s });
      k.noise({ color: 'crackle', at, dur: 0.3, vol: 0.5 * s, type: 'lowpass', f: 1600 * b });
      if (v.variant >= 2) k.metal({ f: p, at, dur: 0.5, vol: 0.04, partials: BELL });
      break;
    case 'steel':
      k.noise({ dur: 0.02, at, vol: 0.28 * s, attack: 0.001, type: 'highpass', f: 3800 });
      k.metal({ f: 1500 * b + v.variant * 120, at, dur: 0.4 + w * 0.2, vol: 0.07 * s });
      k.thump({ f0: 200, f1: 80, at, dur: 0.1, vol: 0.2 * s });
      break;
    case 'shadow':
      k.fm({ f: p, f1: p * 0.7, ratio: 1.41, index: 3 + v.variant, at, dur: 0.45, vol: 0.06 * s });
      k.noise({ at, dur: 0.3, vol: 0.1 * s, f: 1900 * b, f1: 500, q: 7 });
      break;
    case 'ink':
      paper(k, { at, dur: 0.18, vol: 0.16 * s, bright: b });
      for (let i = 0; i < 2 + v.variant; i++)
        k.tone({ f: 300 + i * 40, f1: 950 + i * 60, at: at + 0.05 + i * 0.045, dur: 0.07, vol: 0.045 });
      break;
    case 'qin':
      k.pluck({ f: p, at, dur: 0.6, vol: 0.18 * s, bright: 0.75, bend: 0.02 });
      k.click({ f: 2600 * b, at, vol: 0.12 });
      break;
    case 'blood':
      k.noise({ color: 'brown', at, dur: 0.22, vol: 0.4 * s, f: 520 * b, q: 1.5, trem: [26 + v.variant * 4, 0.8] });
      k.tone({ f: p, f1: p * 0.75, at, dur: 0.4, vol: 0.05 * s, attack: 0.03 });
      break;
    case 'tiger':
      k.tone({ type: 'sawtooth', f: 120, f1: 70, at, dur: 0.35, vol: 0.08 * s, attack: 0.02, out: k.filter('lowpass', 700) });
      k.noise({ color: 'brown', at, dur: 0.2, vol: 0.35 * s, f: 600 * b, q: 1.2, trem: [30, 0.8] });
      break;
    case 'sea':
      k.noise({ color: 'pink', at, dur: 0.45, vol: 0.25 * s, attack: 0.05, type: 'lowpass', f: 1400 * b, f1: 300 });
      k.noise({ color: 'brown', at, dur: 0.2, vol: 0.32 * s, f: 480, q: 1.4, trem: [22, 0.7] });
      break;
    case 'chain':
      for (let i = 0; i < 4 + v.variant; i++) k.metal({ f: k.range(1900, 3300) * b, at: at + i * 0.035, dur: 0.12, vol: 0.022 , partials: TINK });
      k.thump({ f0: 170, f1: 70, at, dur: 0.15, vol: 0.28 * s });
      break;
    case 'petal':
      k.noise({ color: 'crackle', at, dur: 0.2, vol: 0.25 * s, type: 'bandpass', f: 2600 * b, q: 0.7 });
      chime(k, { f: pitch(f, v, 2) * 2, at: at + 0.03, dur: 0.5, vol: 0.04 });
      k.thump({ f0: 160, f1: 70, at, dur: 0.12, vol: 0.2 * s });
      break;
    case 'moon':
      k.fm({ f: p * 2, ratio: 2.76, index: 1.2, index1: 0.05, at, dur: 0.6, vol: 0.04 * s });
      k.noise({ at, dur: 0.12, vol: 0.12 * s, attack: 0.08, f: 3000 * b, f1: 6000, q: 3, linear: true });
      break;
    case 'dance':
      for (let i = 0; i < 3 + v.variant; i++) k.metal({ f: k.range(3000, 4200), at: at + i * 0.05, dur: 0.2, vol: 0.018 , partials: TINK });
      k.tone({ f: p, at, dur: 0.3, vol: 0.03, vib: [6, 0.02] });
      break;
    case 'dragon':
      k.noise({ color: 'brown', at, dur: 0.5, vol: 0.45 * s, attack: 0.02, type: 'lowpass', f: 500 * b, f1: 120 });
      whoosh(k, { at, dur: 0.35, f0: 250, f1: 1600 * b, f2: 400, vol: 0.18 * s, q: 1 });
      k.thump({ f0: 110, f1: 36, at, dur: 0.35, vol: 0.35 * s });
      break;
    case 'needle':
      for (let i = 0; i < 2 + v.variant; i++) k.click({ f: 4200 + i * 500, at: at + i * 0.02, vol: 0.09 });
      k.metal({ f: 3600 * b, at, dur: 0.15, vol: 0.02 });
      break;
    case 'thunder':
      k.noise({ dur: 0.05, at, vol: 0.4 * s, attack: 0.001, type: 'highpass', f: 2500 });
      k.noise({ color: 'crackle', at, dur: 0.3, vol: 0.7 * s, type: 'highpass', f: 1800 });
      k.noise({ color: 'brown', at: at + 0.04, dur: 0.7, vol: 0.35 * s, type: 'lowpass', f: 350, f1: 90, trem: [7, 0.6] });
      break;
    case 'fire':
      k.noise({ color: 'crackle', at, dur: 0.45, vol: 0.5 * s, attack: 0.04, type: 'highpass', f: 1300 });
      k.noise({ color: 'pink', at, dur: 0.4, vol: 0.18 * s, attack: 0.05, f: 500, f1: 2400, q: 1.2 });
      break;
    case 'water':
      k.noise({ at, dur: 0.25, vol: 0.25 * s, attack: 0.005, f: 1300 * b, f1: 500, q: 1.2 });
      k.tone({ f: k.range(500, 700), f1: k.range(1100, 1400), at: at + 0.04, dur: 0.07, vol: 0.05 });
      break;
    case 'hiss':
      k.noise({ at, dur: 0.18, vol: 0.12 * s, attack: 0.01, type: 'highpass', f: 4500 * b });
      k.click({ f: 2200 * b, at, vol: 0.2 });
      break;
    case 'dark':
      k.fm({ f: p * 2, f1: p * 1.4, ratio: 1.41, index: 5, at, dur: 0.6, vol: 0.06 * s });
      k.thump({ f0: 90, f1: 28, at, dur: 0.5, vol: 0.45 * s });
      break;
  }
}

// ───────── the dying away ─────────

/** What lingers after the last blow, `len` seconds. */
export function tail(k: SfxKit, f: Family, at: number, len: number, v: Vary): void {
  const p = pitch(f, v);
  const n = 2 + v.variant;
  switch (f.motif) {
    case 'wind':
      for (let i = 0; i < n; i++)
        k.noise({ color: 'crackle', at: at + i * len * 0.18, dur: 0.12, vol: 0.18 / (i + 1), type: 'highpass', f: 3200 });
      k.tone({ f: pitch(f, v, 4), at, dur: len, vol: 0.02, attack: 0.08, vib: [5, 0.012] });
      break;
    case 'cloud':
      k.noise({ color: 'pink', at, dur: len, vol: 0.08, attack: len * 0.3, type: 'lowpass', f: 900, f1: 250 });
      break;
    case 'taiji':
      bowl(k, { f: p * 0.5, at, dur: len * 1.3, vol: 0.04 });
      break;
    case 'snow':
      k.noise({ color: 'crackle', at, dur: len, vol: 0.25, attack: len * 0.2, type: 'highpass', f: 5000 });
      crystals(k, n, at, len * 0.6, 0.02);
      break;
    case 'bell':
      k.metal({ f: p, at, dur: len * 1.6, vol: 0.04, partials: BELL, drift: 0.003 });
      break;
    case 'stone':
      k.noise({ color: 'brown', at, dur: len, vol: 0.2, attack: 0.02, type: 'lowpass', f: 300, f1: 80 });
      for (let i = 0; i < n; i++) k.click({ f: k.range(900, 1700), at: at + 0.05 + i * 0.07, vol: 0.06 });
      break;
    case 'steel':
      ring(k, { f: p * 4, at, dur: len, vol: 0.025 });
      break;
    case 'shadow':
      k.noise({ at, dur: len, vol: 0.08, attack: len * 0.6, f: 300, f1: 1500, q: 2, linear: true });
      break;
    case 'ink':
      for (let i = 0; i < n; i++) k.tone({ f: 420 - i * 30, f1: 1000, at: at + 0.06 + i * len * 0.2, dur: 0.06, vol: 0.03 });
      k.pluck({ f: p * 0.5, at, dur: len, vol: 0.08, bright: 0.3 });
      break;
    case 'qin':
      k.pluck({ f: pitch(f, v, 2), at, dur: len, vol: 0.09, bright: 0.5 });
      if (v.variant >= 2) k.pluck({ f: pitch(f, v, 4), at: at + 0.12, dur: len, vol: 0.07, bright: 0.5 });
      break;
    case 'blood':
      for (let i = 0; i < n; i++) k.tone({ f: 260 - i * 20, f1: 600, at: at + 0.1 + i * len * 0.22, dur: 0.05, vol: 0.035 });
      k.tone({ type: 'sawtooth', f: p * 0.5, at, dur: len, vol: 0.04, attack: 0.05, out: k.filter('lowpass', 380) });
      break;
    case 'tiger':
      k.tone({ type: 'sawtooth', f: 80, f1: 60, at, dur: len, vol: 0.05, attack: 0.08, vib: [9, 0.05], out: k.filter('lowpass', 500) });
      break;
    case 'sea':
      k.noise({ color: 'pink', at, dur: len * 1.3, vol: 0.12, attack: len * 0.4, type: 'lowpass', f: 900, f1: 300 });
      break;
    case 'chain':
      for (let i = 0; i < n + 2; i++) k.metal({ f: k.range(2000, 3200), at: at + i * 0.06, dur: 0.1, vol: 0.012 , partials: TINK });
      break;
    case 'petal':
      for (let i = 0; i < n; i++) chime(k, { f: pitch(f, v, i * 2) * 2, at: at + i * len * 0.2, dur: 0.6, vol: 0.022 });
      break;
    case 'moon':
      k.noise({ at, dur: len, vol: 0.04, attack: len * 0.5, f: 5000, q: 6 });
      chime(k, { f: pitch(f, v, -1) * 2, at: at + 0.05, dur: len, vol: 0.025 });
      break;
    case 'dance':
      for (let i = 0; i < n; i++) k.metal({ f: k.range(3200, 4400), at: at + i * len * 0.18, dur: 0.18, vol: 0.012 , partials: TINK });
      break;
    case 'dragon':
      k.noise({ color: 'brown', at, dur: len * 1.4, vol: 0.2, attack: 0.1, type: 'lowpass', f: 400, f1: 90, trem: [4, 0.5] });
      break;
    case 'needle':
      for (let i = 0; i < n + 1; i++) k.click({ f: k.range(3000, 5000), at: at + 0.04 + i * 0.05, vol: 0.05 });
      break;
    case 'thunder':
      k.noise({ color: 'brown', at, dur: len * 1.5, vol: 0.2, attack: 0.1, type: 'lowpass', f: 260, f1: 70, trem: [5, 0.6] });
      break;
    case 'fire':
      k.noise({ color: 'crackle', at, dur: len, vol: 0.3, attack: 0.05, type: 'highpass', f: 1500 });
      break;
    case 'water':
      k.noise({ at, dur: len, vol: 0.08, attack: len * 0.3, type: 'lowpass', f: 900, f1: 300 });
      break;
    case 'hiss':
      k.noise({ at, dur: len * 0.6, vol: 0.05, attack: 0.02, type: 'highpass', f: 5000 });
      break;
    case 'dark':
      k.tone({ type: 'sawtooth', f: 55, at, dur: len * 1.3, vol: 0.06, attack: 0.1, out: k.filter('lowpass', 300) });
      break;
  }
}

// ───────── gathering (a wind-up, a power move's breath in) ─────────

/** The art drawing its qi in over `len` seconds, peaking at `at + len`. */
export function gather(k: SfxKit, f: Family, at: number, len: number, v: Vary): void {
  const p = pitch(f, v);
  const L = Math.max(0.12, len);
  k.noise({ at, dur: L + 0.05, vol: 0.12, attack: L, f: 300 * v.bright, f1: 2600 * v.bright, sweep: L, q: 2, linear: true });
  switch (f.motif) {
    case 'wind':
    case 'cloud':
    case 'dragon':
    case 'sea':
      k.noise({ color: 'pink', at, dur: L + 0.05, vol: 0.1, attack: L, f: 400, f1: 1600, sweep: L, q: 1, linear: true });
      break;
    case 'bell':
    case 'stone':
    case 'dark':
    case 'tiger':
      k.tone({ type: 'sawtooth', f: p * 0.25, f1: p * 0.5, at, dur: L + 0.05, vol: 0.05, attack: L, out: k.filter('lowpass', 500) });
      break;
    case 'blood':
      for (let t = 0, i = 0; t < L; t += Math.max(0.09, 0.3 - i * 0.05), i++)
        k.thump({ f0: 90, f1: 45, at: at + t, dur: 0.12, vol: 0.2 });
      break;
    case 'snow':
    case 'moon':
    case 'petal':
    case 'dance':
      k.tone({ f: p * 2, f1: p * 3, at, dur: L + 0.05, vol: 0.025, attack: L });
      break;
    case 'ink':
    case 'qin':
      k.tone({ type: 'triangle', f: p, f1: p * 1.5, at, dur: L + 0.05, vol: 0.03, attack: L, linear: true });
      break;
    default:
      k.tone({ f: p, f1: p * 2, at, dur: L + 0.05, vol: 0.03, attack: L });
  }
}

// ───────── heals and buffs ─────────

/** A heal (`buff` false) or a buff blooming from the art's motif. */
/** The heal/buff arpeggios: pentatonic steps and the gap between them. */
const ARPS: readonly (readonly [readonly number[], number])[] = [
  [[0, 2, 4], 0.07],
  [[0, 4, 7], 0.13],
  [[4, 2, 0], 0.09],
  [[0, 2, 4, 5, 7], 0.05],
  [[0, 7], 0.22],
  [[2, 4, 2, 4], 0.11],
];

export function bloom(k: SfxKit, f: Family, buff: boolean, v: Vary, pattern = buff ? 0 : 3): void {
  const p = pitch(f, v);
  const [steps, gap] = ARPS[pattern % ARPS.length]!;
  if (buff) {
    k.tone({ f: p * 0.5, f1: p, dur: 0.5, vol: 0.06, attack: 0.3 });
    k.noise({ dur: 0.5, vol: 0.08, attack: 0.4, f: 400 * v.bright, f1: 3000 * v.bright, q: 2 });
  } else bowl(k, { f: p * 0.5, dur: 1.4, vol: 0.06 });
  switch (f.motif) {
    case 'wind':
      for (let i = 0; i < 5; i++) k.noise({ color: 'crackle', at: 0.1 + i * 0.16, dur: 0.12, vol: 0.2, type: 'highpass', f: 3000 + i * 300 });
      k.tone({ f: pitch(f, v, 2), at: 0.05, dur: 0.9, vol: 0.03, attack: 0.15, vib: [5, 0.015] });
      break;
    case 'cloud':
    case 'dragon':
      for (let i = 0; i < 3; i++) k.noise({ color: 'pink', at: i * 0.25, dur: 0.4, vol: 0.18, attack: 0.15, type: 'lowpass', f: 900, f1: 400 });
      break;
    case 'blood':
    case 'tiger':
    case 'sea':
      for (let i = 0; i < 4; i++) k.thump({ f0: 85, f1: 42, at: i * (buff ? 0.18 : 0.3), dur: 0.14, vol: 0.25 });
      k.tone({ type: 'sawtooth', f: p * 0.5, at: 0, dur: 0.9, vol: 0.05, attack: 0.3, out: k.filter('lowpass', 420) });
      break;
    case 'ink':
    case 'qin':
      strum(k, p, steps, { vol: 0.09, gap: 0.06, dur: 1.2, bright: 0.45 });
      break;
    case 'bell':
    case 'stone':
      woodblock(k, { f: 700, at: 0, vol: 0.12 });
      k.metal({ f: p, at: 0.05, dur: 1.6, vol: 0.06, partials: BELL, drift: 0.003 });
      break;
    case 'steel':
    case 'chain':
      drum(k, { f: 90, vol: 0.3 });
      drum(k, { f: 90, at: 0.18, vol: 0.25 });
      gong(k, { f: p * 0.5, at: 0.3, dur: 1, vol: 0.06 });
      break;
    case 'snow':
      crystals(k, 10, 0.05, 0.8, 0.025);
      break;
    case 'needle':
      // A smoke puff and the hidden weapons settling.
      k.noise({ color: 'pink', dur: 0.5, vol: 0.2, attack: 0.02, type: 'lowpass', f: 1200, f1: 300 });
      for (let i = 0; i < 4; i++) k.click({ f: 4000, at: 0.2 + i * 0.06, vol: 0.05 });
      break;
    case 'dance':
    case 'moon':
    case 'petal':
      for (let i = 0; i < 4; i++) k.metal({ f: k.range(3000, 4400), at: 0.05 + i * 0.12, dur: 0.3, vol: 0.016 , partials: TINK });
      break;
    default:
      k.fm({ f: p, ratio: 1.41, index: 2, dur: 0.8, vol: 0.04 });
  }
  steps.forEach((d, i) => chime(k, { f: pitch(f, v, d) * 2, at: 0.08 + i * gap, vol: 0.04, dur: buff ? 0.7 : 1.1 }));
}

// ───────── sweeteners: what the move leaves on its target ─────────

/** A small layer per status a move puts on (bleed drips, poison hisses, ice crackles…). */
export function statusLayer(k: SfxKit, status: string, at: number): void {
  switch (status) {
    case 'bleed':
      for (let i = 0; i < 3; i++) k.tone({ f: 240 - i * 25, f1: 560, at: at + 0.08 + i * 0.09, dur: 0.05, vol: 0.03 });
      break;
    case 'poison':
      k.noise({ at, dur: 0.35, vol: 0.06, attack: 0.05, type: 'highpass', f: 5200, trem: [18, 0.6] });
      break;
    case 'slow':
      k.tone({ f: 600, f1: 300, at, dur: 0.3, vol: 0.03 });
      break;
    case 'chill':
    case 'snow-daze':
    case 'frost-trap':
    case 'snow-pride':
      crystals(k, 3, at, 0.15, 0.02);
      break;
    case 'haste':
      k.noise({ at, dur: 0.18, vol: 0.08, attack: 0.03, f: 1500, f1: 6000, q: 3 });
      break;
    case 'charm':
      k.fm({ f: 880, ratio: 1.5, index: 1, at, dur: 0.4, vol: 0.02, attack: 0.05 });
      break;
    case 'ward':
    case 'iron-bell':
      k.metal({ f: 880, at, dur: 0.5, vol: 0.025, partials: BELL });
      break;
    case 'war-cry':
      k.tone({ type: 'sawtooth', f: 160, f1: 220, at, dur: 0.3, vol: 0.04, attack: 0.04, out: k.filter('bandpass', 900, 3) });
      break;
    case 'focus':
      k.tone({ f: 1760, at, dur: 0.4, vol: 0.015, attack: 0.1 });
      break;
    case 'silence':
      k.thump({ f0: 300, f1: 200, at, dur: 0.06, vol: 0.12 });
      break;
    case 'blood-frenzy':
      k.thump({ f0: 80, f1: 40, at, dur: 0.12, vol: 0.2 });
      k.thump({ f0: 80, f1: 40, at: at + 0.16, dur: 0.12, vol: 0.16 });
      break;
  }
}

/** Glassy pings scattered high (ice). */
export function crystals(k: SfxKit, n: number, at: number, spread: number, vol: number): void {
  for (let i = 0; i < n; i++)
    k.tone({ f: k.range(2800, 7200), at: at + k.r() * spread, dur: k.range(0.05, 0.25), vol: vol * k.range(0.5, 1) });
}
